import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Button } from "@mantine/core";
import { FiUpload } from "react-icons/fi";
import { useTheme } from "../../../ThemeContext";
import ProjectName from "../../Dropdowns/ProjectNames";
import { addSoilPrediction, updateSoilPrediction } from "../../../api/api";

const SoilInspectionForm = ({ isOpen, onClose, edit, editData ,handleUpdate}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [checklist, setChecklist] = useState(null);
  const [pname, setPname] = useState('');
  // console.log(pname)
  const [formValues, setFormValues] = useState({
    projectId: edit ? editData.projectId : "",
    siteEngineerName: edit ? editData.siteEngineerName : "",
    inspectorName: edit ? editData.inspectorName : "",
    soilModel: edit ? editData.soilModel : "mohr-coulomb",
    testResult: edit ? editData.testResult : "",
    status: edit ? editData.status : "",
    lastTestDate: edit ? editData.lastTestDate : "",
    comments: edit ? editData.comments : "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("projectId", pname);
    formData.append("siteEngineerName", formValues.siteEngineerName);
    formData.append("qualityInspectorName", formValues.inspectorName);
    formData.append("soilModel", formValues.soilModel);
    formData.append("testResult", formValues.testResult);
    formData.append("status", formValues.status);
    formData.append("lastTestDate", formValues.lastTestDate);
    formData.append("comment", formValues.comments);
    formData.append("checkListFile", checklist);

    try {
      let response;
      if (edit) {
        response = await updateSoilPrediction(formData);
      } else {
        response = await addSoilPrediction(formData);
        console.log(response);
      }

      if (response) {
        onClose();
      } else {
        console.error("Error: Unable to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      handleUpdate()
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-hidden rounded-md ${
                  isDark ? "bg-gray-800 text-white" : "bg-white text-gray-900"
                } text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg`}
              >
                <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="text-center font-semibold leading-6">
                    <h3 className="text-lg py-4">Soil Inspection Form</h3>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
                      <div>
                        <ProjectName
                          pname={pname}
                          handleChange={(e) => setPname(e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block mb-3 text-sm font-medium">Site Engineer Name</label>
                        <input
                          name="siteEngineerName"
                          value={formValues.siteEngineerName}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : ""
                          }`}
                          placeholder="Enter site engineer name"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium">Inspector Name</label>
                        <input
                          name="inspectorName"
                          value={formValues.inspectorName}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : ""
                          }`}
                          placeholder="Enter inspector name"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium">Soil Model</label>
                        <select
                          name="soilModel"
                          value={formValues.soilModel}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : ""
                          }`}
                        >
                          <option value="">--Select--</option>
                          <option value="mohr-coulomb">Mohr-Coulomb</option>
                          <option value="hoek-brow">Hoek-Brow</option>
                          <option value="hardening-soil">Hardening Soil Model</option>
                          <option value="soft-soil">Soft Soil</option>
                          <option value="concrete">Concrete Models</option>
                          <option value="elasto-plastic">Elasto-Plastic Model (Static Liquefaction)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium">Test Result</label>
                        <input
                          name="testResult"
                          value={formValues.testResult}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : ""
                          }`}
                          placeholder="Enter test result"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium">Status</label>
                        <input
                          name="status"
                          value={formValues.status}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : ""
                          }`}
                          placeholder="Enter status"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium">Last Test Date</label>
                        <input
                          type="date"
                          name="lastTestDate"
                          value={formValues.lastTestDate}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : ""
                          }`}
                        />
                      </div>

                      <div >
                        <label className="block mb-2 text-sm font-medium">Checklist</label>
                        <div
                          className={`flex items-center justify-center border ${
                            isDark ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-300"
                          } rounded-md p-2`}
                        >
                          <label className="flex items-center text-sm cursor-pointer">
                            <FiUpload className="mr-2" />
                            {checklist ? checklist.name : "Upload Checklist"}
                            <input
                              type="file"
                              onChange={(e) => setChecklist(e.target.files[0])}
                              className="hidden"
                              accept=".pdf"
                            />
                          </label>
                        </div>
                      </div>

                      <div className="col-span-2">
                        <label className="block mb-2 text-sm font-medium">Comments</label>
                        <textarea
                          name="comments"
                          value={formValues.comments}
                          onChange={handleChange}
                          className={`bg-gray-50 border border-gray-300 text-sm rounded-md block w-full p-2 ${
                            isDark ? "bg-gray-700 text-white border-gray-600" : ""
                          }`}
                          placeholder="Enter comments"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end">
                      <Button variant="outline" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button type="submit" className="ml-3" loading={loading}>
                        {edit ? "Update" : "Save"}
                      </Button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SoilInspectionForm;
